import { Component, forwardRef, OnDestroy, OnInit, Input } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, of, Subscription } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

import { isWhitheSpaces } from '@commons/utility/util';
import { noWhitespaceValidator } from '@commons/utility/utilValidators';

import { PersoReferenceInfo } from '@core/model/perso-reference-info';
import { PhoneValidationResult } from '@core/model/phone-validation-result';
import { CustomResponseDto } from '@core/model/custom-response-dto';
import { ValidatorService } from '../validator.service';

export class PersoReferenceErrorStateMatcher extends ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return control.dirty && form.invalid;
  }
}

@Component({
  selector: 'app-rapi-perso-reference',
  templateUrl: './rapi-perso-reference.component.html',
  styleUrls: ['./rapi-perso-reference.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiPersoReferenceComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiPersoReferenceComponent),
      multi: true,
    },
  ],
})
export class RapiPersoReferenceComponent implements OnInit, ControlValueAccessor, OnDestroy {
  public form: FormGroup;
  public invalidNumberMsg: string;
  public fullNameErrorMsg: string;
  private subscriptions: Subscription[] = [];
  @Input() phoneTooltipMsg: string = '';

  constructor(private formBuilder: FormBuilder, private validatorService: ValidatorService) {}
  ngOnInit() {
    this.initForm();
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  private initForm() {
    this.form = this.formBuilder.group(
      {
        fullName: ['', [Validators.required, noWhitespaceValidator], this._asyncValidateFullName()],
        phoneNumber: ['', [Validators.required]],
      },
      { updateOn: 'blur' }
    );
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );
    this.form.controls['phoneNumber'].setAsyncValidators([this._asyncValidatePhonePipe()]);
    this.form.controls['phoneNumber'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });
    this.form.controls['fullName'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });
  }

  get value(): PersoReferenceInfo {
    return this.form.value;
  }

  set value(value: PersoReferenceInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get fullNameControl() {
    return this.form.controls.fullName;
  }

  get phoneNumberControl() {
    return this.form.controls.phoneNumber;
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  private _asyncValidatePhoneNumber(mobileNumber: string): Observable<ValidationErrors | null> | null {
    return this.validatorService.validatePhoneNumber(mobileNumber).pipe(
      map((response: PhoneValidationResult) => {
        this.invalidNumberMsg = response.message;
        return response.result;
      })
    );
  }

  private _asyncValidatePhonePipe(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(100),
        switchMap((value) => {
          if (value) {
            return this._asyncValidatePhoneNumber(value);
          }
        })
      );
    };
  }

  //TODO: <Arnold> I've to change the name to all the async validation functions like this (*Pipe).
  private _asyncValidateFullName(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const isWhitespace = isWhitheSpaces(control.value);
      if (!isWhitespace) {
        return this.validatorService.nameOrLastNameIsValid(control.value).pipe(
          map((response: CustomResponseDto<boolean>) => {
            this.fullNameErrorMsg = response.message;
            return response.response == false ? { invalidFullName: true } : null;
          })
        );
      } else {
        return of(null);
      }
    };
  }

  ngOnDestroy() {}

  validate(_: FormControl) {
    return this.form.valid ? null : { persoReferenceInfo: { valid: false } };
  }
}
